import { computed, makeObservable, observable } from 'mobx';

import { answerOf, MobxQuery } from 'mobx/MobxQuery';

import {
  getNodeInstance,
  getNodeInstanceTreeNode,
  type NodeInstanceSummary,
  type NodeInstanceTreeNode,
} from 'api/node_instances/operations/getNodeInstanceTree';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

/**
 * Synthetic id for the placeholder shown above a broken (deleted) ancestor.
 * Safe as a sentinel because real node instance ids are positive serials — NAP
 * never assigns -1 — so it can't collide with a genuine node in the tree index.
 */
export const MISSING_ANCESTOR_ID = -1;

/** A node in the navigator tree. `summary` is null only for the missing-ancestor placeholder. */
export interface InstanceTreeNode {
  readonly id: number;
  readonly summary: NodeInstanceSummary | null;
  readonly children: readonly InstanceTreeNode[];
  readonly hasChildren: boolean;
}

const toTreeNode = (node: NodeInstanceTreeNode): InstanceTreeNode => ({
  id: node.nodeInstanceId,
  summary: node,
  children: node.children.map(toTreeNode),
  hasChildren: node.hasChildNodes || node.children.length > 0,
});

interface TreeIndex {
  readonly byId: ReadonlyMap<number, InstanceTreeNode>;
  readonly parentById: ReadonlyMap<number, number | null>;
}

const buildIndex = (root: InstanceTreeNode | null): TreeIndex => {
  const byId = new Map<number, InstanceTreeNode>();
  const parentById = new Map<number, number | null>();
  const walk = (node: InstanceTreeNode, parent: number | null) => {
    byId.set(node.id, node);
    parentById.set(node.id, parent);
    node.children.forEach((child) => walk(child, node.id));
  };
  if (root) walk(root, null);
  return { byId, parentById };
};

const matches = (node: InstanceTreeNode, term: string): boolean =>
  node.summary != null && (node.summary.name.toLowerCase().includes(term) || String(node.id).includes(term));

interface FilteredTree {
  readonly roots: readonly InstanceTreeNode[];
  readonly expandIds: ReadonlySet<number>;
  readonly matchCount: number;
}

/**
 * Prunes the tree to matches + their ancestors and collects the branches to
 * force-expand so every match stays on screen. With no filter, returns the
 * tree unchanged and an empty force-expand set (the model uses the user's own
 * expansion then).
 */
const filterTree = (root: InstanceTreeNode | null, filter: string): FilteredTree => {
  const term = filter.trim().toLowerCase();
  if (!root) return { roots: [], expandIds: new Set(), matchCount: 0 };
  if (term === '') return { roots: [root], expandIds: new Set(), matchCount: 0 };

  const expandIds = new Set<number>();
  let matchCount = 0;
  const prune = (node: InstanceTreeNode): InstanceTreeNode | null => {
    const children = node.children.map(prune).filter((child): child is InstanceTreeNode => child !== null);
    const self = matches(node, term);
    if (self) matchCount += 1;
    if (!self && children.length === 0) return null;
    if (children.length > 0) expandIds.add(node.id);
    return { ...node, children };
  };
  const prunedRoot = prune(root);
  return {
    roots: prunedRoot ? [prunedRoot] : [],
    expandIds,
    matchCount,
  };
};

interface ClimbResult {
  readonly rootId: number;
  /** The id of the missing parent when the chain is broken; null otherwise. */
  readonly missingParentId: number | null;
  /** Every id visited from the entry up to the root, i.e. the branches to expand to show the entry. */
  readonly path: readonly number[];
}

/**
 * Where the one-off climb to the root stands. One tagged field, so "failed but
 * still climbing" or "climbed with an error" cannot be represented.
 */
type Climb = { readonly kind: 'climbing' } | { readonly kind: 'climbed'; readonly result: ClimbResult } | { readonly kind: 'failed'; readonly error: string };

const loadTree = async ({ rootId, missingParentId }: ClimbResult): Promise<InstanceTreeNode> => {
  const root = toTreeNode(await getNodeInstanceTreeNode(rootId));
  if (missingParentId == null) return root;
  return { id: MISSING_ANCESTOR_ID, summary: null, children: [root], hasChildren: true };
};

/**
 * Modal-scoped state for the ancestor + siblings tree navigator. Owns the
 * loaded tree, the active (shown) instance, expansion, and the filter, plus the
 * async load that walks the parent chain to the root and fetches the subtree.
 * The component is a pure observer; all logic here is unit-testable without
 * React.
 *
 * The climb runs once: the root of an instance's tree does not change. Only a
 * failed climb runs again, on the next {@link refresh}. The subtree below it does — statuses move while the page is open — so it is a
 * {@link MobxQuery} that {@link refresh} re-asks, and while it does the tree
 * keeps showing the previous answer. Expansion, selection and filter are keyed
 * by id and so survive a refresh.
 *
 * NOTE: the subtree query arms on observation, like every `MobxQuery`. Reading
 * `roots` outside a reaction loads nothing.
 */
export class InstanceTreeModel {
  private _activeId: number;
  private _climb: Climb = { kind: 'climbing' };
  /** The latest climb; resolves once it settles, success or failure. */
  private _climbing: Promise<void>;
  private readonly _entryId: number;
  private _expandedIds: ReadonlySet<number> = new Set();
  private _filter = '';
  private _refreshToken = 0;
  private readonly _subtree: MobxQuery<ClimbResult | null, InstanceTreeNode | null>;

  constructor(entryId: number) {
    this._activeId = entryId;
    this._entryId = entryId;
    makeObservable<InstanceTreeModel, '_activeId' | '_climb' | '_expandedIds' | '_filter' | '_refreshToken' | '_root' | '_index' | '_filtered'>(this, {
      _activeId: observable,
      _climb: observable.ref,
      _expandedIds: observable.ref,
      _filter: observable,
      _refreshToken: observable,
      _root: computed,
      _index: computed,
      _filtered: computed,
    });
    // Until the climb lands there is no root to ask about; the null answer is
    // local and costs no request.
    this._subtree = new MobxQuery({
      params: () => (this._climb.kind === 'climbed' ? this._climb.result : null),
      fetch: (climb) => (climb ? loadTree(climb) : Promise.resolve(null)),
      debounceMs: 0,
    });
    this._climbing = this.climb();
  }

  get activeId(): number {
    return this._activeId;
  }

  /** A failed climb, or a failed subtree read — the latter shown over the previous tree, if any. */
  get error(): string | null {
    if (this._climb.kind === 'failed') return this._climb.error;
    const value = this._subtree.value;
    return value.kind === 'error' ? value.error.message : null;
  }

  /** The expansion set to render: forced match-paths while filtering, else the user's. */
  get expandedIds(): ReadonlySet<number> {
    return this.filtering ? this._filtered.expandIds : this._expandedIds;
  }

  get filter(): string {
    return this._filter;
  }

  // Pruned/expanded view for the current filter; read by expandedIds, matchCount
  // and roots, so it's memoized to prune the tree once per (root, filter).
  private get _filtered(): FilteredTree {
    return filterTree(this._root, this._filter);
  }

  /** Whether a filter is currently narrowing the tree. */
  get filtering(): boolean {
    return this._filter.trim() !== '';
  }

  // Index of the loaded tree; read by tryActivate + expandPathTo. Memoized so
  // the O(n) walk runs once per loaded tree rather than per lookup.
  private get _index(): TreeIndex {
    return buildIndex(this._root);
  }

  get loading(): boolean {
    const kind = this._subtree.value.kind;
    return this._climb.kind === 'climbing' || kind === 'loading' || kind === 'stale';
  }

  get matchCount(): number {
    return this._filtered.matchCount;
  }

  /** Whether the tree has loaded a root yet. */
  get ready(): boolean {
    return this._root !== null;
  }

  /**
   * Bumped by every {@link refresh}, for the viewer beside the tree to re-read
   * on: re-selecting the active node leaves its id unchanged, so the id alone
   * cannot tell the viewer to reload.
   */
  get refreshToken(): number {
    return this._refreshToken;
  }

  private get _root(): InstanceTreeNode | null {
    return answerOf(this._subtree.value)?.value ?? null;
  }

  get roots(): readonly InstanceTreeNode[] {
    return this._filtered.roots;
  }

  private async climb(): Promise<void> {
    this._climb = { kind: 'climbing' };
    try {
      const result = await this.climbToRoot(this._entryId);
      this._expandedIds = new Set(result.missingParentId == null ? result.path : [...result.path, MISSING_ANCESTOR_ID]);
      this._climb = { kind: 'climbed', result };
    } catch (err) {
      this._climb = { kind: 'failed', error: extractErrorMessage(err, 'Failed to load the instance tree') };
    }
  }

  private async climbToRoot(entryId: number): Promise<ClimbResult> {
    const entry = await getNodeInstance(entryId);
    if (!entry) {
      throw new Error(`Node instance ${entryId} not found`);
    }
    let current = entry;
    // A cyclic/self-referential parent_node_instance_id is a DB-integrity
    // violation that shouldn't happen, but would otherwise hang the tab in an
    // infinite climb hammering the API. Stop the moment we'd revisit a node:
    // root at the highest node reached, no missing-ancestor gap.
    const path = [current.nodeInstanceId];
    const visited = new Set<number>(path);
    while (current.parentNodeInstanceId != null) {
      if (visited.has(current.parentNodeInstanceId)) {
        return { rootId: current.nodeInstanceId, missingParentId: null, path };
      }
      const parent = await getNodeInstance(current.parentNodeInstanceId);
      if (!parent) {
        // A deleted intermediate breaks the chain: root at the highest
        // reachable node and remember the gap for a placeholder.
        return { rootId: current.nodeInstanceId, missingParentId: current.parentNodeInstanceId, path };
      }
      visited.add(parent.nodeInstanceId);
      path.push(parent.nodeInstanceId);
      current = parent;
    }
    return { rootId: current.nodeInstanceId, missingParentId: null, path };
  }

  /** Stops the subtree query now rather than after its grace period. For test hygiene. */
  dispose = (): void => {
    this._subtree.dispose();
  };

  private expandPathTo(id: number): void {
    const { parentById } = this._index;
    const next = new Set(this._expandedIds);
    let current: number | null | undefined = id;
    while (current != null) {
      next.add(current);
      current = parentById.get(current) ?? null;
    }
    this._expandedIds = next;
  }

  /**
   * Re-asks for the tree and signals the viewer to reload, via {@link refreshToken}.
   * After a failed climb there is no root to re-read below, so the climb itself is retried.
   */
  refresh = (): void => {
    this._refreshToken += 1;
    if (this._climb.kind === 'failed') {
      this._climbing = this.climb();
    } else {
      void this._subtree.refresh();
    }
  };

  /** Shows `id`, and refreshes: a click is when the user wants to know how things stand now. */
  setActive = (id: number): void => {
    this._activeId = id;
    this.expandPathTo(id);
    this.refresh();
  };

  setFilter = (value: string): void => {
    this._filter = value;
  };

  toggleExpand = (id: number): void => {
    const next = new Set(this._expandedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this._expandedIds = next;
  };

  /**
   * Activates `id` if it is part of the loaded tree (returns true), otherwise
   * returns false so the caller can route out (e.g. open a new tab). This is
   * how an out-of-tree link — an originating node in a different flow — falls
   * through to its own fresh tree.
   */
  tryActivate(id: number): boolean {
    if (!this._index.byId.has(id)) return false;
    this.setActive(id);
    return true;
  }

  /** Resolves once the climb and any subtree read in flight have settled. For tests. */
  async whenSettled(): Promise<void> {
    await this._climbing;
    await this._subtree.whenSettled();
  }
}
